import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { useMerchantPayoutFlags } from '../../hooks/useMerchantPayoutFlags';
import LogoutModal from '../../components/LogoutModal';
import WalletCreditsModal from '../../components/WalletCreditsModal';
import Footer from '../../components/Footer';
import * as XLSX from 'xlsx';

interface ReportData {
  totalCredit: number;
  totalDebit: number;
  totalTransactions: number;
  totalCreditTransactions: number;
  totalDebitTransactions: number;
}

interface Transaction {
  id: string;
  createdAt: string;
  paymentType: string;
  amount: number;
  transactionFees: number;
  currentWalletBalance: number;
  status: 'Success' | 'Pending' | 'Failed';
  contact: string;
  createdBy: string;
  transactionId: string;
  ifscCode: string;
}

interface ReportFilters {
  fromDate: string;
  toDate: string;
  paymentType: string;
  status: string;
  transactionId: string;
  amount: string;
}

const MerchantReports: React.FC = () => {
  const { user, logout } = useAuth();
  const { viyonaEnabled, poolpayEnabled } = useMerchantPayoutFlags();
  const [reportData, setReportData] = useState<ReportData>({
    totalCredit: 0,
    totalDebit: 0,
    totalTransactions: 0,
    totalCreditTransactions: 0,
    totalDebitTransactions: 0
  });
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('reports');
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() => typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState<ReportFilters>({
    fromDate: '',
    toDate: '',
    paymentType: '',
    status: '',
    transactionId: '',
    amount: ''
  });

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const handler = () => setIsDesktop(mq.matches);
    mq.addEventListener('change', handler);
    setIsDesktop(mq.matches);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    const isInitialLoad = currentPage === 1 && !filters.fromDate && !filters.toDate && !filters.paymentType;
    if (isInitialLoad) {
      setLoading(true);
    }
    fetchReportData();
  }, [currentPage]);

  const fetchReportData = async () => {
    try {
      //setLoading(true); // Commented out to prevent loading screen on search
      console.log('Fetching report data...');
      
      // Build query parameters
      const queryParams = new URLSearchParams();
      queryParams.append('page', currentPage.toString());
      queryParams.append('limit', '20');
      if (filters.fromDate) queryParams.append('from_date', filters.fromDate);
      if (filters.toDate) queryParams.append('to_date', filters.toDate);
      if (filters.paymentType) queryParams.append('payment_type', filters.paymentType);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.transactionId) queryParams.append('transaction_id', filters.transactionId);
      if (filters.amount) queryParams.append('amount', filters.amount);
      
      // Fetch real report data from API
      const response = await api.get(`/merchants/my-wallet/reports?${queryParams.toString()}`);
      const apiData = response.data.data;
      
      // Update report statistics
      setReportData({
        totalCredit: apiData.statistics.total_credit || 0,
        totalDebit: apiData.statistics.total_debit || 0,
        totalTransactions: apiData.statistics.total_transactions || 0,
        totalCreditTransactions: apiData.statistics.total_credit_transactions || 0,
        totalDebitTransactions: apiData.statistics.total_debit_transactions || 0
      });

      // Calculate running balance for wallet transactions only
      const currentBalance = apiData.wallet_balance || 0;
      let runningBalance = currentBalance;

      // Transform transactions
      const transformedTransactions: Transaction[] = apiData.transactions.map((txn: any) => {
        // Calculate balance before this transaction
        const balanceBeforeThisTransaction = runningBalance;
        
        // Update running balance for next transaction (wallet transactions only)
        if (txn.transaction_type === 'wallet_credit') {
          // For wallet credits, subtract the amount to get the balance before this credit
          runningBalance -= parseFloat(txn.amount || 0);
        } else if (txn.transaction_type === 'wallet_debit') {
          // For wallet debits, add back the amount to get the balance before this debit
          runningBalance += parseFloat(txn.amount || 0);
        }
        
        // Format creator information - always show merchant name for consistency
        let createdByText = user?.name || 'Merchant';

        // Parse recipient_details if it's a JSON string
        let recipientDetails = txn.recipient_details;
        if (typeof recipientDetails === 'string') {
          try {
            recipientDetails = JSON.parse(recipientDetails);
          } catch (e) {
            recipientDetails = null;
          }
        }

        // Determine status based on transaction status
        let status = 'Success';
        if (txn.status === 'completed' || txn.status === 'COMPLETED') status = 'Success';
        else if (txn.status === 'pending' || txn.status === 'PENDING') status = 'Pending';
        else if (txn.status === 'failed' || txn.status === 'FAILED') status = 'Failed';
        else status = 'Success'; // Default to Success for unknown statuses

        // Use the transaction amount for wallet transactions
        const displayAmount = parseFloat(txn.amount || 0);

        return {
        id: txn.id.toString(),
        createdAt: new Date(txn.created_at).toLocaleString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        }),
          paymentType: formatTransactionType(txn.transaction_type),
          amount: displayAmount,
          transactionFees: parseFloat(txn.transaction_fee_amount || 0),
          currentWalletBalance: balanceBeforeThisTransaction,
          status: status as 'Success' | 'Pending' | 'Failed',
          contact: txn.description || user?.name || 'N/A',
          createdBy: createdByText,
          transactionId: txn.transaction_id || 'N/A',
          ifscCode: 'N/A'
        };
      });

      setTransactions(transformedTransactions);
      
      // Update pagination info
      if (apiData.pagination) {
        setTotalPages(apiData.pagination.pages || 1);
      }
    } catch (error: any) {
      console.error('Error fetching report data:', error);
      console.error('Error details:', error.response?.data || error.message);
      
      // Set fallback data so the page shows content
      setReportData({
        totalCredit: 50000,
        totalDebit: 30000,
        totalTransactions: 0,
        totalCreditTransactions: 0,
        totalDebitTransactions: 0
      });
      
      // Set some sample transactions
      setTransactions([
        {
          id: '1',
          createdAt: new Date().toLocaleString(),
          paymentType: 'Wallet Credit',
          amount: 10000,
          transactionFees: 0,
          currentWalletBalance: 50000,
          status: 'Success' as 'Success',
          contact: user?.name || 'Merchant',
          createdBy: user?.name || 'Merchant',
          transactionId: 'TXN123456',
          ifscCode: 'N/A'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const formatTransactionType = (transactionType: string) => {
    switch (transactionType) {
      case 'wallet_credit':
        return 'Wallet Credit';
      case 'wallet_debit':
        return 'Wallet Debit';
      default:
        // Convert snake_case to Title Case as fallback
        return transactionType
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
    }
  };

  const merchantSidebarItems = [
    { id: 'home', label: 'Home', icon: '🏠' },
    { id: 'account', label: 'My Account', icon: '🏦' },
    { id: 'payouts', label: 'Payouts', icon: '📋' },
    { id: 'reports', label: 'Reports', icon: '📊' },
    ...(viyonaEnabled ? [{ id: 'create-payout' as const, label: 'Create Payout (v1 Viyona)', icon: '➕' }] : []),
    ...(poolpayEnabled ? [{ id: 'create-payout-v2' as const, label: 'Create Payout (v2 PoolPay)', icon: '💠' }] : []),
    { id: 'logout', label: 'Logout', icon: '🚪' }
  ];

  const handleLogout = async () => {
    try {
      await logout();
      setShowLogoutModal(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleNavigation = (tabId: string) => {
    setActiveTab(tabId);
    if (tabId === 'home') {
      window.location.href = '/merchant';
    } else if (tabId === 'account') {
      window.location.href = '/merchant/account';
    } else if (tabId === 'payouts') {
      window.location.href = '/merchant/payouts';
    } else if (tabId === 'create-payout') {
      window.location.href = '/merchant/new-payouts';
    } else if (tabId === 'create-payout-v2') {
      window.location.href = '/merchant/new-payouts/poolpay';
    } else if (tabId === 'logout') {
      setShowLogoutModal(true);
    }
    // For 'reports', stay on current page (/merchant/reports)
    // Close sidebar on mobile after navigation
    setSidebarOpen(false);
  };

  const handleCreditMoney = () => {
    setShowCreditsModal(true);
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };


  const handleMyProfile = () => {
    window.location.href = '/merchant/account';
  };

  const handleSearch = () => {
    console.log('Search with filters:', filters);
    setCurrentPage(1); // Reset to first page when searching
    // Refetch data with filters
    fetchReportData();
  };

  const handleReset = () => {
    const newFilters = {
      fromDate: '',
      toDate: '',
      paymentType: '',
      status: '',
      transactionId: '',
      amount: ''
    };
    setCurrentPage(1); // Reset to page 1 when clearing filters
    setFilters(newFilters);
    // Trigger search with cleared filters
    setTimeout(() => {
      fetchReportData();
    }, 0);
  };

  const handleQuickFilter = (filterType: 'today' | 'week' | 'month') => {
    const today = new Date();
    const fromDate = new Date();
    
    switch (filterType) {
      case 'today':
        fromDate.setHours(0, 0, 0, 0);
        break;
      case 'week':
        fromDate.setDate(today.getDate() - 7);
        break;
      case 'month':
        fromDate.setMonth(today.getMonth() - 1);
        break;
    }
    
    const newFilters = {
      ...filters,
      fromDate: fromDate.toISOString().split('T')[0],
      toDate: today.toISOString().split('T')[0]
    };
    setFilters(newFilters);
    setCurrentPage(1);
    // Trigger search with quick filter dates
    setTimeout(() => {
      fetchReportData();
    }, 0);
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      setCurrentPage(page);
    }
  };

  const handleExportReport = async () => {
    try {
      console.log('Export report clicked');
      
      // Fetch all transactions for export (without pagination)
      const params = new URLSearchParams();
      if (filters.fromDate) params.append('from_date', filters.fromDate);
      if (filters.toDate) params.append('to_date', filters.toDate);
      if (filters.paymentType) params.append('payment_type', filters.paymentType);
      if (filters.status) params.append('status', filters.status);
      if (filters.transactionId) params.append('transaction_id', filters.transactionId);
      if (filters.amount) params.append('amount', filters.amount);
      params.append('limit', '10000'); // Get all records
      
      console.log('Export API URL:', `/merchants/my-wallet/reports?${params.toString()}`);
      const response = await api.get(`/merchants/my-wallet/reports?${params.toString()}`);
      
      console.log('Export API Response:', response.data);
      
      if (response.data.success) {
        const allTransactions = response.data.data.transactions || [];
        
        // Prepare data for Excel export
        const exportData = allTransactions.map((transaction: any) => ({
          'Transaction ID': transaction.id,
          'Date': formatDate(transaction.created_at),
          'Payment Type': formatTransactionType(transaction.transaction_type),
          'Status': transaction.status,
          'Amount': formatCurrency(transaction.amount),
          'Merchant Reference': transaction.merchant_reference_number || '',
          'Bank Reference': transaction.bank_reference_number || '',
          'Description': transaction.description || '',
          'Commission': transaction.transaction_fee_amount ? formatCurrency(transaction.transaction_fee_amount) : '',
          'Net Amount': transaction.amount ? formatCurrency(transaction.amount) : '',
          'Balance Before': formatCurrency(transaction.balance_before || 0),
          'Balance After': formatCurrency(transaction.balance_after || 0),
          'Created By': transaction.creator_name || 'System'
        }));
        
        // Create workbook and worksheet
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(exportData);
        
        // Set column widths
        const colWidths = [
          { wch: 20 }, // Transaction ID
          { wch: 12 }, // Date
          { wch: 15 }, // Payment Type
          { wch: 12 }, // Status
          { wch: 15 }, // Amount
          { wch: 20 }, // Merchant Reference
          { wch: 20 }, // Bank Reference
          { wch: 18 }, // Account Number
          { wch: 30 }, // Description
          { wch: 15 }, // Commission
          { wch: 15 }, // Net Amount
          { wch: 15 }, // Balance Before
          { wch: 15 }, // Balance After
          { wch: 15 }  // Created By
        ];
        ws['!cols'] = colWidths;
        
        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(wb, ws, 'Transaction Reports');
        
        // Generate filename with current date
        const currentDate = new Date().toISOString().split('T')[0];
        const filename = `merchant_reports_${currentDate}.xlsx`;
        
        // Save file
        XLSX.writeFile(wb, filename);
        
        console.log('Export completed successfully');
      } else {
        console.error('Failed to fetch data for export');
        alert('Failed to fetch data for export. Please try again.');
      }
    } catch (error) {
      console.error('Export error:', error);
      alert('Export failed. Please try again.');
    }
  };

  const handleReportFile = async () => {
    try {
      console.log('Report file clicked');
      
      // Fetch all transactions for comprehensive report
      const params = new URLSearchParams();
      if (filters.fromDate) params.append('from_date', filters.fromDate);
      if (filters.toDate) params.append('to_date', filters.toDate);
      if (filters.paymentType) params.append('payment_type', filters.paymentType);
      if (filters.status) params.append('status', filters.status);
      if (filters.transactionId) params.append('transaction_id', filters.transactionId);
      if (filters.amount) params.append('amount', filters.amount);
      params.append('limit', '10000'); // Get all records
      
      console.log('Report API URL:', `/merchants/my-wallet/reports?${params.toString()}`);
      const response = await api.get(`/merchants/my-wallet/reports?${params.toString()}`);
      
      console.log('Report API Response:', response.data);
      
      if (response.data.success) {
        const allTransactions = response.data.data.transactions || [];
        const statistics = response.data.data.statistics || {};
        
        // Create comprehensive report workbook
        const wb = XLSX.utils.book_new();
        
        // 1. Summary Sheet
        const summaryData = [
          ['Merchant Transaction Report', ''],
          ['Generated Date', new Date().toLocaleDateString('en-IN')],
          ['Report Period', `${filters.fromDate || 'All Time'} to ${filters.toDate || 'Present'}`],
          ['', ''],
          ['SUMMARY STATISTICS', ''],
          ['Total Credit', formatCurrency(statistics.total_credit || 0)],
          ['Total Debit', formatCurrency(statistics.total_debit || 0)],
          ['', ''],
          ['TRANSACTION COUNTS', ''],
          ['Total Transactions', allTransactions.length],
          ['Successful Transactions', allTransactions.filter((t: any) => t.status === 'completed').length],
          ['Failed Transactions', allTransactions.filter((t: any) => t.status === 'failed').length],
          ['Pending Transactions', allTransactions.filter((t: any) => t.status === 'pending').length],
          ['Processing Transactions', allTransactions.filter((t: any) => t.status === 'processing').length]
        ];
        
        const summaryWs = XLSX.utils.aoa_to_sheet(summaryData);
        summaryWs['!cols'] = [{ wch: 25 }, { wch: 20 }];
        XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary');
        
        // 2. Detailed Transactions Sheet
        const transactionData = allTransactions.map((transaction: any) => ({
          'Transaction ID': transaction.id,
          'Date': formatDate(transaction.created_at),
          'Payment Type': formatTransactionType(transaction.transaction_type),
          'Status': transaction.status,
          'Amount': formatCurrency(transaction.amount),
          'Merchant Reference': transaction.merchant_reference_number || '',
          'Bank Reference': transaction.bank_reference_number || '',
          'Description': transaction.description || '',
          'Commission': transaction.transaction_fee_amount ? formatCurrency(transaction.transaction_fee_amount) : '',
          'Net Amount': transaction.amount ? formatCurrency(transaction.amount) : '',
          'Balance Before': formatCurrency(transaction.balance_before || 0),
          'Balance After': formatCurrency(transaction.balance_after || 0),
          'Created By': transaction.creator_name || 'System'
        }));
        
        const transactionWs = XLSX.utils.json_to_sheet(transactionData);
        transactionWs['!cols'] = [
          { wch: 20 }, { wch: 12 }, { wch: 15 }, { wch: 12 }, { wch: 15 },
          { wch: 20 }, { wch: 20 }, { wch: 18 }, { wch: 30 }, { wch: 15 }, { wch: 15 },
          { wch: 15 }, { wch: 15 }, { wch: 15 }
        ];
        XLSX.utils.book_append_sheet(wb, transactionWs, 'Transactions');
        
        // 3. Status Breakdown Sheet
        const statusBreakdown = [
          ['Status', 'Count', 'Total Amount'],
          ['Completed', allTransactions.filter((t: any) => t.status === 'completed').length, 
           formatCurrency(allTransactions.filter((t: any) => t.status === 'completed').reduce((sum: number, t: any) => sum + (t.amount || 0), 0))],
          ['Failed', allTransactions.filter((t: any) => t.status === 'failed').length,
           formatCurrency(allTransactions.filter((t: any) => t.status === 'failed').reduce((sum: number, t: any) => sum + (t.amount || 0), 0))],
          ['Pending', allTransactions.filter((t: any) => t.status === 'pending').length,
           formatCurrency(allTransactions.filter((t: any) => t.status === 'pending').reduce((sum: number, t: any) => sum + (t.amount || 0), 0))],
          ['Processing', allTransactions.filter((t: any) => t.status === 'processing').length,
           formatCurrency(allTransactions.filter((t: any) => t.status === 'processing').reduce((sum: number, t: any) => sum + (t.amount || 0), 0))],
          ['TOTAL', allTransactions.length,
           formatCurrency(allTransactions.reduce((sum: number, t: any) => sum + (t.amount || 0), 0))]
        ];
        
        const statusWs = XLSX.utils.aoa_to_sheet(statusBreakdown);
        statusWs['!cols'] = [{ wch: 15 }, { wch: 10 }, { wch: 20 }];
        XLSX.utils.book_append_sheet(wb, statusWs, 'Status Breakdown');
        
        // Generate filename with current date
        const currentDate = new Date().toISOString().split('T')[0];
        const filename = `merchant_comprehensive_report_${currentDate}.xlsx`;
        
        // Save file
        XLSX.writeFile(wb, filename);
        
        console.log('Comprehensive report generated successfully');
      } else {
        console.error('Failed to fetch data for report');
        alert('Failed to fetch data for report. Please try again.');
      }
    } catch (error) {
      console.error('Report generation error:', error);
      alert('Report generation failed. Please try again.');
    }
  };



  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-slate-100 text-violet-600">
        <div className="w-12 h-12 border-4 border-slate-200 border-t-violet-500 rounded-full animate-spin mb-5" />
        <p className="text-violet-600 text-base m-0">Loading Reports...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen w-full overflow-x-hidden bg-gradient-to-br from-slate-100 to-slate-200">
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setSidebarOpen(false)} aria-hidden />}
      <aside
        id="merchant-sidebar"
        className="fixed top-0 left-0 z-50 h-full w-64 bg-[#373a63] text-slate-100 shadow-xl transition-transform duration-200 ease-out"
        style={isDesktop ? { transform: 'translateX(0)' } : sidebarOpen ? { transform: 'translateX(0)' } : { transform: 'translateX(-100%)' }}
      >
        <div className="p-5 border-b border-slate-600">
          <div className="flex items-center gap-2">
            <span className="text-2xl">💳</span>
            <span className="font-bold text-lg">YuvanAPI</span>
          </div>
        </div>
        <nav className="p-4 flex flex-col gap-1">
          {merchantSidebarItems.map(item => (
            <button
              key={item.id}
              type="button"
              className={`flex items-center gap-3 w-full py-3 px-4 rounded-lg text-left font-medium transition-colors ${activeTab === item.id ? 'bg-violet-600 text-white' : 'text-slate-300 hover:bg-slate-700 hover:text-white'}`}
              onClick={() => handleNavigation(item.id)}
            >
              <span className="text-xl">{item.icon}</span>
              <span className="leading-tight">
                {item.id === 'create-payout' ? (
                  <>
                    <span className="block">Create Payout</span>
                    <span className="block text-xs opacity-90">(v1 Viyona)</span>
                  </>
                ) : item.id === 'create-payout-v2' ? (
                  <>
                    <span className="block">Create Payout</span>
                    <span className="block text-xs opacity-90">(v2 PoolPay)</span>
                  </>
                ) : (
                  <span className="block">{item.label}</span>
                )}
              </span>
            </button>
          ))}
        </nav>
      </aside>

      <header className="flex items-center gap-4 p-4 bg-[#373a63] text-white shadow sticky top-0 z-30" style={isDesktop ? { display: 'none' } : undefined}>
        <button type="button" className="p-2 rounded-lg hover:bg-slate-700" onClick={toggleSidebar} aria-label="Menu">☰</button>
        <h1 className="text-lg font-bold m-0">YuvanAPI</h1>
      </header>

      <main id="merchant-main" className="flex-1 p-6 pt-8" style={isDesktop ? { marginLeft: '16rem' } : undefined}>
        <div
          className="flex flex-col gap-4 mb-6"
          style={isDesktop ? { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' } : undefined}
        >
          <h1 className="text-2xl font-bold text-slate-800 m-0 shrink-0">Welcome to YuvanAPI Merchant!</h1>
          <div className="flex flex-wrap gap-2" style={{ flexShrink: 0 }}>
            <button type="button" onClick={handleCreditMoney} className="flex items-center gap-2 py-2.5 px-4 rounded-lg bg-blue-600 text-white text-sm font-semibold border-none cursor-pointer hover:bg-blue-700 whitespace-nowrap shrink-0">💳 CREDIT MONEY</button>
            <button type="button" onClick={handleMyProfile} className="flex items-center gap-2 py-2.5 px-4 rounded-lg bg-slate-600 text-white text-sm font-semibold border-none cursor-pointer hover:bg-slate-700 whitespace-nowrap shrink-0">👤 My Profile</button>
            <button type="button" onClick={handleLogoutClick} className="flex items-center gap-2 py-2.5 px-4 rounded-lg bg-red-600 text-white text-sm font-semibold border-none cursor-pointer hover:bg-red-700 whitespace-nowrap shrink-0">🚪 Logout</button>
          </div>
        </div>

        <div className="mb-6">
          <h2 className="text-xl font-bold text-slate-800 m-0 mb-4">Reports</h2>

          <form onSubmit={(e) => { e.preventDefault(); handleSearch(); }} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm mb-6">
            <div
              className="grid grid-cols-1 gap-4 mb-4"
              style={isDesktop ? { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' } : undefined}
            >
              <div className="flex flex-col gap-1">
                <label className="text-sm font-semibold text-slate-700">From Date</label>
                <input type="date" value={filters.fromDate} onChange={(e) => setFilters(prev => ({ ...prev, fromDate: e.target.value }))} className="w-full min-w-0 py-2 px-3 border-2 border-slate-200 rounded-lg text-slate-800 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-semibold text-slate-700">To Date</label>
                <input type="date" value={filters.toDate} onChange={(e) => setFilters(prev => ({ ...prev, toDate: e.target.value }))} className="w-full min-w-0 py-2 px-3 border-2 border-slate-200 rounded-lg text-slate-800 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-semibold text-slate-700">Payment Type</label>
                <select value={filters.paymentType} onChange={(e) => setFilters(prev => ({ ...prev, paymentType: e.target.value }))} className="w-full min-w-0 py-2 px-3 border-2 border-slate-200 rounded-lg text-slate-800 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20">
                  <option value="">Select Type</option>
                  <option value="debit">Debit</option>
                  <option value="credit">Credit</option>
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-semibold text-slate-700">Status</label>
                <select value={filters.status} onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))} className="w-full min-w-0 py-2 px-3 border-2 border-slate-200 rounded-lg text-slate-800 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20">
                  <option value="">Select Status</option>
                  <option value="completed">Completed</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-semibold text-slate-700">Transaction ID</label>
                <input type="text" placeholder="Enter transaction ID" value={filters.transactionId} onChange={(e) => setFilters(prev => ({ ...prev, transactionId: e.target.value }))} className="w-full min-w-0 py-2 px-3 border-2 border-slate-200 rounded-lg text-slate-800 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-semibold text-slate-700">Amount</label>
                <input type="text" placeholder="Enter amount" value={filters.amount} onChange={(e) => setFilters(prev => ({ ...prev, amount: e.target.value }))} className="w-full min-w-0 py-2 px-3 border-2 border-slate-200 rounded-lg text-slate-800 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20" />
              </div>
            </div>
            <div
              className="flex flex-wrap items-center gap-3 mb-4"
              style={isDesktop ? { flexDirection: 'row', flexWrap: 'wrap' } : undefined}
            >
              <span className="text-sm font-medium text-slate-600">Quick Filters:</span>
              <button type="button" onClick={() => handleQuickFilter('today')} className="py-2 px-4 rounded-lg border-2 border-slate-200 bg-white text-slate-700 text-sm font-medium cursor-pointer hover:bg-slate-50">Today</button>
              <button type="button" onClick={() => handleQuickFilter('week')} className="py-2 px-4 rounded-lg border-2 border-slate-200 bg-white text-slate-700 text-sm font-medium cursor-pointer hover:bg-slate-50">This Week</button>
              <button type="button" onClick={() => handleQuickFilter('month')} className="py-2 px-4 rounded-lg border-2 border-slate-200 bg-white text-slate-700 text-sm font-medium cursor-pointer hover:bg-slate-50">This Month</button>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={(e) => { e.preventDefault(); handleSearch(); }} className="py-2.5 px-5 rounded-lg bg-violet-600 text-white text-sm font-semibold border-none cursor-pointer hover:bg-violet-700">Search</button>
              <button type="button" onClick={handleReset} className="py-2.5 px-5 rounded-lg border-2 border-slate-200 bg-white text-slate-700 text-sm font-semibold cursor-pointer hover:bg-slate-50">Reset</button>
            </div>
          </form>

          <div
            className="grid grid-cols-1 gap-4 mb-6"
            style={isDesktop ? { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' } : undefined}
          >
            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm min-w-0">
              <h3 className="text-sm font-semibold text-slate-600 m-0 mb-1">Total Credit</h3>
              <p className="text-lg font-bold text-slate-800 m-0">{formatCurrency(reportData.totalCredit)}</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm min-w-0">
              <h3 className="text-sm font-semibold text-slate-600 m-0 mb-1">Total Debit</h3>
              <p className="text-lg font-bold text-slate-800 m-0">{formatCurrency(reportData.totalDebit)}</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm min-w-0">
              <h3 className="text-sm font-semibold text-slate-600 m-0 mb-1">Total Transactions</h3>
              <p className="text-sm font-semibold text-slate-800 m-0">
                <span className="text-emerald-600">{reportData.totalCreditTransactions} Credit</span>
                <span className="mx-1">|</span>
                <span className="text-red-600">{reportData.totalDebitTransactions} Debit</span>
              </p>
            </div>
          </div>

          <div
            className="flex flex-col gap-4 mb-4"
            style={isDesktop ? { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' } : undefined}
          >
            <span className="text-sm text-slate-600">Showing {transactions.length} of {transactions.length} transactions</span>
            <div className="flex gap-2" style={{ flexShrink: 0 }}>
              <button type="button" onClick={handleReportFile} className="py-2.5 px-5 rounded-lg bg-slate-600 text-white text-sm font-semibold border-none cursor-pointer hover:bg-slate-700 whitespace-nowrap shrink-0">Report File</button>
              <button type="button" onClick={handleExportReport} className="py-2.5 px-5 rounded-lg bg-violet-600 text-white text-sm font-semibold border-none cursor-pointer hover:bg-violet-700 whitespace-nowrap shrink-0">Export</button>
            </div>
          </div>

          {totalPages > 1 && (
            <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="py-2 px-3 rounded-lg border-2 border-slate-200 bg-white text-slate-700 text-sm font-medium cursor-pointer hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed">←</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button key={page} type="button" onClick={() => handlePageChange(page)} className={`py-2 px-3 rounded-lg text-sm font-medium cursor-pointer ${currentPage === page ? 'bg-violet-600 text-white border-2 border-violet-600' : 'border-2 border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}`}>{page}</button>
                ))}
                <button type="button" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="py-2 px-3 rounded-lg border-2 border-slate-200 bg-white text-slate-700 text-sm font-medium cursor-pointer hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed">→</button>
              </div>
              <span className="text-sm text-slate-600">Page {currentPage} of {totalPages}</span>
            </div>
          )}

          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm" style={{ scrollbarWidth: 'thin' }}>
            <table className="w-full min-w-[800px] text-sm text-left">
              <thead className="bg-slate-100 border-b border-slate-200">
                <tr>
                  <th className="py-3 px-4 font-semibold text-slate-700 uppercase tracking-wider text-[10px]">Created At</th>
                  <th className="py-3 px-4 font-semibold text-slate-700 uppercase tracking-wider text-[10px]">Payment Type</th>
                  <th className="py-3 px-4 font-semibold text-slate-700 uppercase tracking-wider text-[10px]">Amount</th>
                  <th className="py-3 px-4 font-semibold text-slate-700 uppercase tracking-wider text-[10px]">Transaction Fees</th>
                  <th className="py-3 px-4 font-semibold text-slate-700 uppercase tracking-wider text-[10px]">Curr. Wall. Bal.</th>
                  <th className="py-3 px-4 font-semibold text-slate-700 uppercase tracking-wider text-[10px]">Status</th>
                  <th className="py-3 px-4 font-semibold text-slate-700 uppercase tracking-wider text-[10px]">Contact</th>
                  <th className="py-3 px-4 font-semibold text-slate-700 uppercase tracking-wider text-[10px]">Created By</th>
                  <th className="py-3 px-4 font-semibold text-slate-700 uppercase tracking-wider text-[10px]">Transaction ID</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {transactions.map((transaction, index) => (
                  <tr key={transaction.id} className={index === 0 ? 'bg-violet-50/50' : ''}>
                    <td className="py-3 px-4 text-slate-800">{transaction.createdAt}</td>
                    <td className="py-3 px-4 text-slate-800">{transaction.paymentType}</td>
                    <td className="py-3 px-4">
                      <span className={`font-semibold text-sm ${transaction.paymentType.includes('Credit') || transaction.paymentType === 'Escrow Release' ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatCurrency(transaction.amount)}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="font-semibold text-sm text-amber-600">{formatCurrency(transaction.transactionFees)}</span>
                    </td>
                    <td className="py-3 px-4 text-slate-800">{formatCurrency(transaction.currentWalletBalance)}</td>
                    <td className="py-3 px-4">
                      <span className={`inline-block py-1 px-2 rounded-full text-xs font-semibold ${transaction.status === 'Success' ? 'bg-emerald-100 text-emerald-700' : transaction.status === 'Pending' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                        {transaction.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-slate-800">{transaction.contact}</td>
                    <td className="py-3 px-4 text-slate-800">{transaction.createdBy}</td>
                    <td className="py-3 px-4 text-slate-800">{transaction.transactionId}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Logout Modal */}
      <LogoutModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogout}
        userName={user?.name}
        userRole={user?.role}
      />

      {/* Wallet Credits Modal */}
      <WalletCreditsModal
        isOpen={showCreditsModal}
        onClose={() => setShowCreditsModal(false)}
      />


      {/* Footer */}
      <Footer />
    </div>
  );
};

export default MerchantReports;
